<script>
import {
  avatarColourSets,
  stringToPredefinedColourSet,
} from '@/helpers/color-helpers'

export default {
  props: {
    size: {
      type: String,
      default: 'sm',
    },
    left: {
      type: Boolean,
      default: false,
    },
    badge: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    badgeColor: {
      type: String,
      default: 'green',
    },
    badgeIcon: {
      type: String,
      default: undefined,
    },
    badgeDot: {
      type: Boolean,
      default: false,
    },
    badgeOffsetX: {
      type: String,
      default: undefined,
    },
    badgeOffsetY: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      required: true,
    },
    imageUrl: {
      type: String,
      default: null,
    },
    defaultColor: {
      type: Boolean,
      default: false,
    },
    generic: {
      type: String,
      default: null,
    },
    transparentOutline: {
      type: Boolean,
      default: false,
    },
    border: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
      default: '',
    },
    fontSize: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      sizeList: {
        xs: '24',
        sm: '32',
        md: '40',
        lg: '56',
        xl: '62',
        xxl: '74',
      },
      fontSizeList: {
        xs: '10px',
        sm: '12px',
        md: '14px',
        lg: '14px',
        xl: '14px',
        xxl: '16px',
      },
      initials: 'NA',
      color: avatarColourSets.ateam_purple,
      tooltipEnabled: false,
      grayAvatarColor: {
        background: 'var(--gray-bg)',
        foreground: 'var(--gray-fg)',
      },
    }
  },
  computed: {
    compFontSize() {
      if (this.fontSize) return `${this.fontSize}px`

      let fontFromAvatarSize

      try {
        fontFromAvatarSize = this.fontSizeList[this.size]
      } catch {
        fontFromAvatarSize = '12px'
      }

      return fontFromAvatarSize
    },
    compAvatarSize() {
      if (!isNaN(this.size)) return this.size

      return this.sizeList[this.size]
    },
    compInitialTextStyle() {
      return {
        'font-size': this.compFontSize,
        color: this.color?.foreground,
      }
    },
    hasCandidate() {
      return this.name && this.name.trim() !== ''
    },
    avatarColor() {
      return this.hasCandidate ? this.color : this.grayAvatarColor
    },
  },
  watch: {
    name: {
      handler() {
        this.initUserObj()
      },
      deep: true,
    },
  },
  mounted() {
    this.initUserObj()
  },
  methods: {
    generateInitials() {
      if (!this.name) return 'NA'
      const allNames = this.name.trim().split(' ')
      const initials = allNames.reduce((acc, curr, index) => {
        if (index === 0 || index === allNames.length - 1) {
          acc = `${acc}${curr.charAt(0).toUpperCase()}`
        }
        return acc
      }, '')
      return initials
    },
    initUserObj() {
      this.initials = this.generateInitials()
      if (this.hasCandidate) {
        this.color = stringToPredefinedColourSet(this.name)
      }
    },
  },
}
</script>

<template>
  <div>
    <v-tooltip
      v-model="tooltipEnabled"
      :disabled="!tooltip"
      :data-tooltip-text="tooltipText"
      top
    >
      <template v-slot:activator="{ on }">
        <v-badge
          :value="badge"
          bottom
          :dot="badgeDot"
          :icon="badgeIcon"
          :color="badgeColor"
          :offset-x="badgeOffsetX"
          :offset-y="badgeOffsetY"
          :data-badge-icon="badgeIcon"
          :data-badge-color="badgeColor"
          bordered
          overlap
          avatar
        >
          <v-avatar
            data-testid="user-avatar"
            :left="left"
            :rounded="rounded"
            :size="compAvatarSize"
            :color="avatarColor.background"
            :class="[
              transparentOutline ? 'avatarOutline' : '',
              border ? 'border border--white border--width-2' : '',
              rounded ? 'rounded-lg' : '',
              clickable ? 'pointer' : '',
              clickable ? 'avatarcontainer' : '',
            ]"
            v-on="on"
            @click="$emit('click')"
          >
            <slot>
              <div if="clickable" class="zoomicon"
                ><v-icon color="white">mdi-magnify-plus-outline</v-icon>asd</div
              >

              <v-img
                v-if="imageUrl"
                :src="imageUrl"
                :alt="name + ' profile picture'"
              />
              <span
                v-else-if="hasCandidate"
                data-testid="user-avatar-initials"
                :class="['font-weight-regular text-uppercase']"
                :style="compInitialTextStyle"
              >
                {{ initials }}
              </span>
              <v-icon
                v-else
                data-testid="user-avatar-placeholder"
                :color="avatarColor.foreground"
              >
                mdi-account
              </v-icon>
            </slot>
          </v-avatar>
        </v-badge>
      </template>
      <slot name="tooltip">
        <span> {{ tooltipText }} </span>
      </slot>
    </v-tooltip>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

div {
  --gray-bg: #{$color-neutral-grey-200};
  --gray-fg: #{$color-gray-550};
}

.avatarOutline {
  /* background: rgba(255, 255, 255, 0.3); */
  -webkit-box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.34);
  -moz-box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.34);
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.34);
}
.pointer {
  cursor: pointer;
}
.zoomicon {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  display: none;
  width: 100%;
  height: 100%;
  padding: auto;
  margin: auto;
  color: #fff;
  text-align: center;
  text-shadow: 0 0 10px #000;
  background-color: rgba(0, 0, 0, 0.3);
}
.avatarcontainer:hover {
  .zoomicon {
    display: block !important;
  }
}
</style>
